import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cnames from 'classnames';
import {DateRangePicker, dateUtils} from '@homeaway/react-date-pickers';
import './style.less';
import DayAvailability from '../DayAvailability/index';
import CheckInCheckOut from '../CheckInCheckOut';
import DatePickerLabels from '../DatePickerLabels';

const createDatesState = (previousCheckIn, previousCheckOut, pendingCheckIn, pendingCheckOut) => ({
    previousCheckIn,
    previousCheckOut,
    pendingCheckIn,
    pendingCheckOut
});

export default class DatePicker extends Component {
    static propTypes = {
        isMobile: PropTypes.bool,
        checkIn: PropTypes.string,
        onInputCheckIn: PropTypes.func.isRequired,
        checkOut: PropTypes.string,
        onInputCheckOut: PropTypes.func.isRequired,
        labels: PropTypes.shape({
            checkIn: PropTypes.string,
            checkOut: PropTypes.string,
            labels: PropTypes.string,
            arrival: PropTypes.string,
            departure: PropTypes.string
        }),
        locale: PropTypes.string,
        consolidateInputs: PropTypes.bool,
        smartDatePicker: PropTypes.bool,
        hideClearDatesBtn: PropTypes.bool,
        stabTheme: PropTypes.bool,
        inputIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        calendarLabels: PropTypes.shape({}),
        animatedCue: PropTypes.bool,
        displayYearFirst: PropTypes.bool
    };

    static defaultProps = {
        id: 'searchbox-datepicker'
    }

    state = {
        showContinueButton: !!(this.props.checkIn && this.props.checkOut),
        isDatePickerOpen: false,
        previousCheckIn: this.props.checkIn,
        previousCheckOut: this.props.checkOut,
        pendingCheckIn: null,
        pendingCheckOut: null,
        readOnly: false
    }

    triggerInputs= (checkInDate, checkOutDate) => {
        this.props.onInputCheckIn(checkInDate);
        this.props.onInputCheckOut(checkOutDate);
    }

    handleDateRangeChange = (checkInDate, checkOutDate) => {

        if (this.props.isMobile) {
            this.triggerInputs(checkInDate, checkOutDate);
        } else {
            const timer = 300;
            setTimeout(() => {
                this.triggerInputs(checkInDate, checkOutDate);
            }, timer);
        }

        const hasBothDates = !!(checkInDate && checkOutDate);

        let state = {
            showContinueButton: hasBothDates
        };

        if (hasBothDates) {
            state.pendingCheckIn = checkInDate;
            state.pendingCheckOut = checkOutDate;
        }

        this.setState(state);
    };

    handleDatePickerOpen = () => {
        this.setState({
            isDatePickerOpen: true,
            readOnly: this.props.isMobile
        });
    }

    handleDatePickerClose = (...args) => {
        const {
            pendingCheckIn,
            pendingCheckOut
        } = this.state;

        let datesState = {};

        if (pendingCheckIn && pendingCheckOut) {
            datesState = createDatesState(pendingCheckIn, pendingCheckOut, null, null);
        }

        this.setState({
            isDatePickerOpen: false,
            readOnly: false,
            ...datesState
        });

        if (this.props.onClose) {
            this.props.onClose(...args);
        }
    }

    handleDateRangeClear = () => {
        this.setState(createDatesState(null, null, null, null));
    }

    handleCancel = () => {
        this.triggerInputs(this.state.previousCheckIn, this.state.previousCheckOut);
    };

    handleSelectionModeChange = (isCheckout) => {
        if (isCheckout) {
            this.setState({selectingCheckIn: false, selectingCheckOut: true});
        } else {
            this.setState({selectingCheckIn: true, selectingCheckOut: false});
        }
    };

    render() {
        const {
            checkIn,
            checkOut,
            isMobile,
            labels,
            locale,
            isOpen,
            consolidateInputs,
            hideClearDatesBtn,
            smartDatePicker,
            inputIcon,
            stabTheme,
            calendarLabels,
            animatedCue,
            id,
            displayYearFirst
        } = this.props;

        const {
            showContinueButton,
            isDatePickerOpen,
            readOnly,
            selectingCheckIn,
            selectingCheckOut
        } = this.state;

        const className = cnames('DatePicker__range', {
            'consolidate-inputs-dates': consolidateInputs,
            'hide-clear-btn': hideClearDatesBtn,
            'stab-theme': stabTheme,
            'animated-cue': !consolidateInputs && animatedCue,
            'check-in': !consolidateInputs && animatedCue && selectingCheckIn,
            'check-out': !consolidateInputs && animatedCue && selectingCheckOut
        });

        const dateFormatOptions = consolidateInputs ?
            {month: 'short', day: 'numeric', ...(smartDatePicker ? {weekday: 'short'} : {})} :
            {year: 'numeric', month: '2-digit', day: '2-digit'};

        let checkInDate;
        let checkOutDate;

        if (smartDatePicker) {
            checkInDate = dateUtils.getLocaleDateString(checkIn, locale, dateFormatOptions);
            checkOutDate = dateUtils.getLocaleDateString(checkOut, locale, dateFormatOptions);
        }

        let modalCheckInCheckOut = null;

        if (stabTheme) {
            modalCheckInCheckOut = (
                <DatePickerLabels
                    checkIn={checkIn}
                    checkOut={checkOut}
                    arrivalLabel={labels.arrival}
                    departureLabel={labels.departure}
                />
            );
        } else if (smartDatePicker) {
            modalCheckInCheckOut = (
                <CheckInCheckOut
                    checkIn={checkInDate}
                    checkInLabel={labels.checkIn}
                    checkOutLabel={labels.checkOut}
                    checkOut={checkOutDate}
                    isOpen={isDatePickerOpen}
                    inModal
                />
            );
        }

        return (
            <div className={className}>
                <DateRangePicker
                    fadeOutDuration={0}
                    startDate={checkIn}
                    endDate={checkOut}
                    inputLabel1={consolidateInputs ? labels.dates : labels.checkIn}
                    inputLabel2={labels.checkOut}
                    inputName1={'checkin'}
                    inputName2={'checkout'}
                    onDateRangeChange={this.handleDateRangeChange}
                    onDateRangeClear={this.handleDateRangeClear}
                    onSelectionModeChange={this.handleSelectionModeChange}
                    disableTextInput
                    clearButtonLabel={labels.labels}
                    continueButtonLabel={labels.done}
                    dayTemplateComponent={DayAvailability}
                    inputIcon1={inputIcon}
                    inputIcon2={inputIcon}
                    dateFormatOptions={dateFormatOptions}
                    onOpen={this.handleDatePickerOpen}
                    onClose={this.handleDatePickerClose}
                    onCancel={this.handleCancel}
                    onSubmit={this.handleSubmit}
                    modalTitle={modalCheckInCheckOut}
                    {...{isMobile, locale, showContinueButton, isOpen, consolidateInputs, readOnly, calendarLabels, id, displayYearFirst}}
                />
                { (!stabTheme && (smartDatePicker && (checkIn || isDatePickerOpen))) &&
                    <CheckInCheckOut
                        checkIn={checkInDate}
                        checkInLabel={labels.checkIn}
                        checkOutLabel={labels.checkOut}
                        checkOut={checkOutDate}
                        isOpen={isDatePickerOpen}
                    />
                }
            </div>
        );
    }
}
